import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ComebackPreview, useSavedRoomsApi } from '../../../../common/hooks/use-saved-rooms-api.hook';
import { StatusBadge } from '../../../../components/design-system/admin';

/**
 * The comeback email a room would get today, exactly as it would be sent —
 * with whether it would go out, and why not. The link in it opens nothing.
 * For a room the shopper did not share, the API draws a neutral frame where
 * the shopper sees their own room, and this says so.
 */
export function ComebackPreviewDialog({ open, roomUuid, onClose }: { open: boolean; roomUuid?: string; onClose: () => void }) {
    const { t } = useTranslation();
    const api = useSavedRoomsApi();
    const [preview, setPreview] = useState<ComebackPreview | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!open) return;
        let alive = true;
        setPreview(null);
        setError(false);
        api.comebackPreview(roomUuid)
            .then((p) => alive && setPreview(p))
            .catch(() => alive && setError(true));
        return () => {
            alive = false;
        };
    }, [open, roomUuid, api]);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[92vh] overflow-hidden sm:max-w-[680px]" data-testid="comeback-preview">
                <DialogHeader>
                    <DialogTitle>{t('SAVED_ROOMS.COMEBACK.PREVIEW_TITLE')}</DialogTitle>
                    <DialogDescription>
                        {preview
                            ? t('SAVED_ROOMS.COMEBACK.PREVIEW_FOR', { room: preview.room.name, email: preview.room.email || (preview.room.hasEmail ? t('SAVED_ROOMS.EMAIL_PRIVATE') : t('SAVED_ROOMS.ANONYMOUS')) })
                            : t('SAVED_ROOMS.COMEBACK.PREVIEW_DESC')}
                    </DialogDescription>
                </DialogHeader>
                {error && <p className="text-sm text-muted-foreground">{t('SAVED_ROOMS.COMEBACK.PREVIEW_NONE')}</p>}
                {!preview && !error && <Skeleton className="h-[420px] w-full" />}
                {preview && (
                    <div className="flex min-h-0 flex-col gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <StatusBadge tone={preview.eligible ? 'success' : 'neutral'}>
                                {preview.eligible ? t('SAVED_ROOMS.COMEBACK.WOULD_SEND') : t('SAVED_ROOMS.COMEBACK.WOULD_NOT_SEND')}
                            </StatusBadge>
                            {preview.reasons.map((r) => (
                                <span key={r} className="text-xs text-muted-foreground">
                                    {t(`SAVED_ROOMS.COMEBACK.REASON.${r.toUpperCase()}`)}
                                </span>
                            ))}
                        </div>
                        <p className="text-sm">
                            <span className="text-muted-foreground">{t('SAVED_ROOMS.COMEBACK.SUBJECT')} </span>
                            {preview.subject}
                        </p>
                        {preview.sample && <p className="text-xs text-muted-foreground">{t('SAVED_ROOMS.COMEBACK.SAMPLE')}</p>}
                        {preview.room.shared === false && (
                            <p className="text-xs text-muted-foreground" data-testid="comeback-preview-private">{t('SAVED_ROOMS.COMEBACK.PRIVATE_ROOM')}</p>
                        )}
                        <iframe
                            title={t('SAVED_ROOMS.COMEBACK.PREVIEW_TITLE')}
                            srcDoc={preview.html}
                            sandbox=""
                            className="h-[58vh] w-full rounded-md border bg-white"
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
